/**
 * HTML to React Content Converter
 * Helps convert HTML/CSS content from the old site to React-compatible format
 */

export const convertHtmlToReact = (htmlContent) => {
  // Replace class with className
  let reactContent = htmlContent.replace(/class=/g, 'className=');
  
  // Replace for with htmlFor
  reactContent = reactContent.replace(/for=/g, 'htmlFor=');
  
  // Convert style strings to objects (basic conversion)
  reactContent = reactContent.replace(/style="([^"]*)"/g, (match, styles) => {
    const styleObject = styles.split(';').reduce((acc, style) => {
      if (style.trim()) {
        const [property, value] = style.split(':').map(s => s.trim());
        const camelCaseProperty = property.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
        acc[camelCaseProperty] = value;
      }
      return acc;
    }, {});
    return `style={${JSON.stringify(styleObject)}}`;
  });
  
  // Self-closing tags
  reactContent = reactContent.replace(/<img([^>]*)>/g, '<img$1 />');
  reactContent = reactContent.replace(/<br>/g, '<br />');
  reactContent = reactContent.replace(/<hr>/g, '<hr />');
  reactContent = reactContent.replace(/<input([^>]*)>/g, '<input$1 />');
  
  return reactContent;
};

/**
 * Extract meta information from HTML page
 */
export const extractMetaFromHtml = (htmlString) => {
  const meta = {
    title: '',
    description: '',
    keywords: '',
    ogImage: ''
  };
  
  // Extract title
  const titleMatch = htmlString.match(/<title>([^<]*)<\/title>/i);
  if (titleMatch) {
    meta.title = titleMatch[1];
  }
  
  // Extract meta description
  const descMatch = htmlString.match(/<meta\s+name="description"\s+content="([^"]*)"/i);
  if (descMatch) {
    meta.description = descMatch[1];
  }
  
  // Extract keywords
  const keywordsMatch = htmlString.match(/<meta\s+name="keywords"\s+content="([^"]*)"/i);
  if (keywordsMatch) {
    meta.keywords = keywordsMatch[1];
  }
  
  // Extract OG image
  const ogImageMatch = htmlString.match(/<meta\s+property="og:image"\s+content="([^"]*)"/i);
  if (ogImageMatch) {
    meta.ogImage = ogImageMatch[1];
  }
  
  return meta;
};

/**
 * Create a React component from HTML content
 */
export const createServicePageData = (htmlString, url) => {
  const meta = extractMetaFromHtml(htmlString);
  
  // Extract main content (usually within main tag or specific div)
  let content = '';
  const mainMatch = htmlString.match(/<main[^>]*>([\s\S]*?)<\/main>/i);
  if (mainMatch) {
    content = mainMatch[1];
  } else {
    // Fallback to body content
    const bodyMatch = htmlString.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
    if (bodyMatch) {
      content = bodyMatch[1];
    }
  }
  
  // Convert to React-compatible format
  const reactContent = convertHtmlToReact(content);
  
  return {
    title: meta.title,
    metaDescription: meta.description,
    keywords: meta.keywords,
    heroImage: meta.ogImage,
    content: reactContent,
    canonicalUrl: `https://paradise-performance.com${url}`
  };
};

/**
 * Service categories and structure for navigation
 */
export const serviceCategories = {
  europeanRepair: {
    title: 'European Car Repair',
    path: '/services/european-car-repair',
    brands: [
      { name: 'BMW', path: '/services/european-car-repair/bmw' },
      { name: 'Mercedes', path: '/services/european-car-repair/mercedes' },
      { name: 'Audi', path: '/services/european-car-repair/audi' },
      { name: 'Porsche', path: '/services/european-car-repair/porsche' },
      { name: 'Volkswagen', path: '/services/european-car-repair/volkswagen' },
      { name: 'Mini Cooper', path: '/services/european-car-repair/mini-cooper' },
      { name: 'Land Rover', path: '/services/european-car-repair/land-rover' },
      { name: 'Jaguar', path: '/services/european-car-repair/jaguar' },
      { name: 'Volvo', path: '/services/european-car-repair/volvo' }
    ]
  },
  dieselRepair: {
    title: 'Diesel Repair Services',
    path: '/repair/diesel',
    brands: [
      { name: 'Ford Powerstroke', path: '/services/diesel/ford-powerstroke' },
      { name: 'Dodge Ram Cummins', path: '/services/diesel/dodge-cummins' },
      { name: 'Chevy GMC Duramax', path: '/services/diesel/chevy-duramax' }
    ]
  },
  generalServices: {
    title: 'General Services',
    path: '/services',
    services: [
      { name: 'Oil Change', path: '/services/oil-change' },
      { name: 'Brake Repair', path: '/services/brake-repair' },
      { name: 'Transmission Repair', path: '/services/transmission-repair' },
      { name: 'AC Repair', path: '/services/car-ac-repair' },
      { name: 'Battery Service', path: '/services/battery-service' },
      { name: 'Clutch Repair', path: '/services/clutch-repair' },
      { name: 'Diagnostic Check Engine Light', path: '/services/diagnostic-check-engine-light' },
      { name: 'Suspension Repair', path: '/services/suspension-repair-dallas' },
      { name: 'Timing Belt/Chain Service', path: '/services/timing-belt-chain-service' },
      { name: 'Differential/Driveline Repair', path: '/services/differential-driveline-repair' },
      { name: 'Radiator Service', path: '/services/radiator' },
      { name: 'Wheel and Tire', path: '/services/wheel-and-tire' },
      { name: 'European Performance Tuning', path: '/services/european-performance-tuning' },
      { name: 'Factory Scheduled Maintenance', path: '/services/factory-scheduled-maintenance' }
    ]
  }
};
